import type {
	AutoCompleteBlurEvent,
	AutoCompleteCloseEvent,
	AutoCompleteHandle,
	AutoCompleteProps,
} from "@tql/ui-components-react/kendo/dropdowns";
import { AutoComplete } from "@tql/ui-components-react/kendo/dropdowns";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDebounceValue } from "usehooks-ts";

import { usePostalCodeSearch } from "~/features/address-book/hooks/use-address-book.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";

const countryOrder: Record<string, number> = { USA: 1, CAN: 2, MEX: 3 };

type SearchResult = {
	data: ZipCode;
	displayValue: string;
};

type PostalCodeSearchProps = {
	onSelectionChange?: (selectedItem: ZipCode | null) => void;
} & AutoCompleteProps;

export const PostalCodeSearch = ({
	onSelectionChange,
	value: propsValue,
	...props
}: PostalCodeSearchProps) => {
	const [value, setValue] = useState(propsValue?.toString() ?? "");
	const [selectedResult, setSelectedResult] = useState<SearchResult | null>(
		propsValue
			? {
					data: {} as ZipCode,
					displayValue: propsValue.toString(),
				}
			: null,
	);

	const isUserTypingRef = useRef(false);
	const hasPendingInputRef = useRef(false);
	const autoCompleteRef = useRef<AutoCompleteHandle | null>(null);
	const lastFocusedItemRef = useRef<SearchResult | null>(null);
	const wasOpenOnCloseRef = useRef(false);
	const pendingCommitQueryRef = useRef<string | null>(null);

	React.useEffect(() => {
		if (propsValue === undefined) {
			return;
		}

		const stringValue = propsValue?.toString() ?? "";
		if (
			(isUserTypingRef.current || hasPendingInputRef.current) &&
			!stringValue
		) {
			return;
		}

		isUserTypingRef.current = false;
		hasPendingInputRef.current = false;
		setValue(stringValue);
		setSelectedResult(
			stringValue
				? {
						data: {} as ZipCode,
						displayValue: stringValue,
					}
				: null,
		);
	}, [propsValue]);

	const [debouncedValue] = useDebounceValue(value, 300);

	const isSearchEnabled = useMemo(() => {
		if (!debouncedValue.trim()) return false;
		return !(selectedResult && debouncedValue === selectedResult.displayValue);
	}, [debouncedValue, selectedResult]);

	const { data: postalCodes = [], isFetching } = usePostalCodeSearch(
		debouncedValue,
		isSearchEnabled,
	);

	const searchResults: SearchResult[] = useMemo(
		() =>
			postalCodes
				.map((zipCode) => ({
					data: zipCode,
					displayValue: `${zipCode.postalCode} - ${zipCode.city.name}, ${zipCode.city.state.abbreviation}, ${zipCode.city.state.countryCode}`,
				}))
				.sort(
					(a, b) =>
						(countryOrder[a.data.city.state.countryCode] ?? 99) -
						(countryOrder[b.data.city.state.countryCode] ?? 99),
				),
		[postalCodes],
	);

	const handleChange = useCallback(
		(event: { target: { value: string } }) => {
			const selectedValue = event.target.value;
			const hasTypedValue = selectedValue.trim().length > 0;

			isUserTypingRef.current = hasTypedValue;

			const result =
				searchResults.find((r) => r.displayValue === selectedValue) ?? null;

			pendingCommitQueryRef.current = null;

			if (result) {
				isUserTypingRef.current = false;
				hasPendingInputRef.current = false;
				lastFocusedItemRef.current = null;
			} else {
				hasPendingInputRef.current = hasTypedValue;
			}

			setValue(selectedValue);
			setSelectedResult(result);

			onSelectionChange?.(result?.data ?? null);
		},
		[searchResults, onSelectionChange],
	);

	const commitFocusedItem = useCallback(() => {
		const explicitFocusedItem =
			(autoCompleteRef.current?.state?.focusedItem as
				| SearchResult
				| undefined) ?? lastFocusedItemRef.current;
		lastFocusedItemRef.current = null;

		const isOpen =
			autoCompleteRef.current?.state?.opened === true ||
			wasOpenOnCloseRef.current;
		wasOpenOnCloseRef.current = false;

		const fallbackItem =
			!explicitFocusedItem && isOpen && value.trim().length > 0
				? searchResults[0]
				: undefined;

		const focusedItem = explicitFocusedItem ?? fallbackItem;
		if (!focusedItem?.displayValue) {
			const trimmed = value.trim();
			if (trimmed.length > 0 && searchResults.length === 0) {
				pendingCommitQueryRef.current = value;
			}
			return;
		}

		if (value === focusedItem.displayValue) return;

		const inData = searchResults.some(
			(r) => r.displayValue === focusedItem.displayValue,
		);
		if (!inData) return;

		handleChange({ target: { value: focusedItem.displayValue } });
	}, [handleChange, searchResults, value]);

	const handleClose = useCallback(
		(event: AutoCompleteCloseEvent) => {
			const focusedItem = autoCompleteRef.current?.state?.focusedItem as
				| SearchResult
				| undefined;
			if (focusedItem) {
				lastFocusedItemRef.current = focusedItem;
			}
			wasOpenOnCloseRef.current = true;
			props.onClose?.(event);
		},
		[props],
	);

	const handleBlur = useCallback(
		(event: AutoCompleteBlurEvent) => {
			commitFocusedItem();
			props.onBlur?.(event);
		},
		[commitFocusedItem, props],
	);

	React.useEffect(() => {
		const pending = pendingCommitQueryRef.current;
		if (!pending) return;
		if (debouncedValue !== pending) return;
		if (isFetching) return;
		const first = searchResults[0];
		if (!first) return;
		if (value !== pending) {
			pendingCommitQueryRef.current = null;
			return;
		}
		pendingCommitQueryRef.current = null;
		handleChange({ target: { value: first.displayValue } });
	}, [debouncedValue, handleChange, isFetching, searchResults, value]);

	return (
		<AutoComplete
			{...props}
			ref={autoCompleteRef}
			data={searchResults}
			loading={isFetching}
			onBlur={handleBlur}
			onChange={handleChange}
			onClose={handleClose}
			textField={"displayValue"}
			value={value}
		/>
	);
};
